/**
 * DriverEarningsPage — `/driver-portal/earnings`.
 *
 * Live · GET /v1/driver-portal/me/earnings. Computes the period total from
 * shifts + delivered stops + per-operator driver_earnings_config (per-stop
 * allowance, base, on-time bonus). PAYE / pension deductions ride with the
 * service-finance integration in a future slice — gross == net for now.
 */
import { type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Award, Calendar, Lock, TrendingUp, Wallet } from 'lucide-react';
import { Link } from 'react-router-dom';

import { ApiError } from '../../lib/api/client';
import { getDriverPortalEarnings } from '../../lib/api/driver-portal';
import { PageHeader } from '../shared/StaffUI';
import { useDriverPortalContext } from './DriverPortalLayout';

function formatNaira(minor: string | null | undefined, currency = 'NGN'): string {
  if (minor == null) return '—';
  try {
    const big = BigInt(minor);
    const major = big / 100n;
    const remainder = big % 100n;
    const padded = remainder.toString().padStart(2, '0');
    const symbol = currency === 'NGN' ? '₦' : `${currency} `;
    return `${symbol}${major.toLocaleString()}.${padded}`;
  } catch {
    return '—';
  }
}

export function DriverEarningsPage(): JSX.Element {
  const { driverId } = useDriverPortalContext();
  const earnings = useQuery({
    queryKey: ['driver-portal', 'earnings', driverId],
    queryFn: () => getDriverPortalEarnings(driverId),
    retry: false,
    staleTime: 60_000,
  });

  const denied =
    earnings.isError &&
    earnings.error instanceof ApiError &&
    (earnings.error.status === 401 || earnings.error.status === 403);
  const data = earnings.data ?? null;

  const period = data
    ? `${new Date(data.periodStart).toLocaleDateString()} → ${new Date(data.periodEnd).toLocaleDateString()}`
    : '—';

  return (
    <div>
      <PageHeader
        title="Earnings & payslips"
        description="Your earnings, computed from shifts and delivered stops."
        actions={
          <span className="inline-flex items-center gap-1.5 text-xs text-muted bg-gray-100 px-3 py-1.5 rounded-lg">
            <Lock className="w-3 h-3" /> Read-only
          </span>
        }
      />

      {denied ? (
        <p className="text-sm text-amber-700 mt-4">Permission required: <code className="font-mono">Driver/readEarnings</code></p>
      ) : null}

      {/* Period card */}
      <div className="bg-gradient-to-br from-nipost-green to-nipost-green-dark text-white rounded-2xl p-6 mb-6 mt-4">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-white/70">Pay period</p>
            <h2 className="text-2xl font-extrabold" data-testid="period-range">{period}</h2>
          </div>
          <span className="bg-white/15 text-xs font-bold uppercase tracking-widest px-2 py-1 rounded">
            {data?.configSource === 'OPERATOR_OVERRIDE' ? 'Operator rates' : 'Default rates'}
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Pill label="Gross"      value={formatNaira(data?.grossMinorUnits ?? null, data?.currency)} />
          <Pill label="Bonuses"    value={formatNaira(data?.bonusMinorUnits ?? null, data?.currency)} />
          <Pill label="Net"        value={formatNaira(data?.netMinorUnits ?? null, data?.currency)} big />
          <Pill label="Hours · Stops" value={data ? `${data.hoursWorked}h · ${data.stopsDelivered}/${data.stopsAttempted}` : '—'} />
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Breakdown */}
        <div className="lg:col-span-2 bg-white border border-border rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Wallet className="w-4 h-4 text-nipost-green" /> Breakdown
          </h3>
          <table className="w-full text-sm" data-testid="earnings-breakdown">
            <thead className="text-[10px] uppercase tracking-wider text-muted">
              <tr>
                <th className="text-left py-2">Line</th>
                <th className="text-right py-2">Qty</th>
                <th className="text-right py-2">Rate</th>
                <th className="text-right py-2">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(data?.breakdown ?? []).map((line) => (
                <tr key={line.label}>
                  <td className="py-2">{line.label}</td>
                  <td className="py-2 text-right text-muted">{line.qty}</td>
                  <td className="py-2 text-right text-muted">{formatNaira(line.rateMinor, data?.currency)}</td>
                  <td className="py-2 text-right font-medium">{formatNaira(line.amountMinor, data?.currency)}</td>
                </tr>
              ))}
              {(data?.breakdown.length ?? 0) === 0 ? (
                <tr><td colSpan={4} className="py-6 text-center text-muted">No earnings yet for this period.</td></tr>
              ) : null}
            </tbody>
          </table>
          <p className="text-[11px] text-muted mt-4 flex items-center gap-1.5">
            <TrendingUp className="w-3 h-3" />
            Pays into your account on file. Change banking details in{' '}
            <Link to="/driver-portal/profile" className="underline text-nipost-green">
              Profile &amp; Banking
            </Link>{' '}(24-hour cooling-off applies).
          </p>
        </div>

        {/* Right column */}
        <div className="space-y-4">
          <div className="bg-white border border-border rounded-xl p-5">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Award className="w-4 h-4 text-nipost-gold" /> On-time bonus
            </h3>
            <p className="text-2xl font-extrabold text-gray-900">
              {data?.onTimePercent != null ? `${data.onTimePercent}%` : '—'}
            </p>
            <p className="text-[11px] text-muted mt-1">Threshold: 90% on-time triggers the bonus.</p>
          </div>

          <div className="bg-white border border-border rounded-xl p-3">
            <p className="text-[10px] uppercase tracking-widest text-muted font-semibold px-2 py-1.5">
              Period summary
            </p>
            <div className="px-2 py-3 text-xs text-muted text-center">
              <Calendar className="w-6 h-6 mx-auto text-gray-300 mb-2" />
              <p>{period}</p>
              <p className="mt-1">{data ? `${data.stopsDelivered} delivered / ${data.stopsAttempted} attempted` : '—'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Pill(props: { readonly label: string; readonly value: string; readonly big?: boolean }): JSX.Element {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-white/70">{props.label}</p>
      <p className={`${props.big ? 'text-3xl font-extrabold' : 'text-2xl font-bold'}`}>{props.value}</p>
    </div>
  );
}
